/**
 * @description TowTech SO Overview (LYN-400 / LYN-402 / LYN-422 / LYN-426 / LYN-438). Company and All.
 *
 * Key features:
 * - Desktop: Volume + comparison left. Grid + Response right, bottoms aligned.
 * - Next row: Profit vs pace left of Account Mix. Dots use the quadrant key.
 *   Drivers | Trucks filters the dots. All starts as one point per company.
 * - Phone: Volume, comparison, Profit vs pace, Account Mix, Response.
 * - Today band (highlights + Current Activity) uses live-day pace
 * - KPI strip: Tows / average response / % late / Needs attention
 * - Grid: existing Analysis tiles plus two Utilization tiles. Mobile stacks.
 * - Account Type is the population. Usual follows the same cut.
 * - Advanced panel holds Police Late, Regular Late, and Minimum calls.
 * - All and Regular show Regular Late. Police shows Police Late only.
 * - All scores police jobs against Regular late.
 * - Response pie BI line is a centered caption at the bottom of the card.
 * - Early is under half the late minutes. Scheduled leave response and late.
 * - Needs Attention rotates Analysis, utilization, and cross-report lines. One sentence.
 *
 * @dependencies jobsService, overviewUtilizationService, scope1Dashboard,
 *   scope1Highlights, overviewUtilization, assetsCrew, ReportingCharts
 *
 * @notes All fans out created companies on this sales org via X-Company-Id.
 *   Company view clocks companies.timezone. All clocks the viewer zone.
 *   Location is not the clock. Type All counts loaded job rows in the
 *   display-zone civil window — same path as Police and Regular. jobs stays
 *   SoR. Utilization reads stored windows only. No second writer.
 *   No invented ETA. Do not print R12, UTC, WoW, stamp, or window.
 *   Profit vs pace drill does not move the nav company switcher.
 *   Account Mix is calls only. Jobs carry no invoice. Grain follows
 *   the Account Type filter. All and Regular stay on types. An exact
 *   type token is accounts. No bar click. Hour, day, and week volume
 *   usual clips the prior window to the elapsed clock.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  CHART,
  ChartCard,
  ChartEmpty,
  ChartLegend,
  ReportingBarChart,
  ReportingLineChart,
  ReportingPieChart,
  ReportingScatterChart,
  chartEventRow
} from '../../components/reporting/ReportingCharts';
import jobsService from '../../services/api/jobsService';
import overviewUtilizationService from '../../services/api/overviewUtilizationService';
import {
  ACCOUNT_TYPE_ALL,
  DEFAULT_POLICE_LATE_MINUTES,
  DEFAULT_REGULAR_LATE_MINUTES,
  OVERVIEW_MIN_CALLS_DEFAULT,
  RESPONSE_COMPLETED,
  RESPONSE_ON_SCENE,
  TIME_FILTERS,
  TODAY_COLOR,
  USUAL_COLOR,
  VOLUME_STAMP,
  accountTypeOptions,
  chartTimeZone,
  companyClockZone,
  comparisonTitle,
  comparisonWindowForFilter,
  filterJobsByAccountType,
  formatSeconds,
  formatVolume,
  historyComparisonRows,
  isEmptyVolume,
  jobsInWindow,
  jobsOnUsualWeekday,
  jobsOnZonedToday,
  lateRateByType,
  meanDuration,
  mergeAggregateBuckets,
  mergeHourComparison,
  mergeWeekdayComparison,
  pieLateSentence,
  pipelineGrainForFilter,
  previousWindowForFilter,
  responsePieSlices,
  salesOrgCompanies,
  salesOrgHasMultipleTimeZones,
  showsComparisonChart,
  parseMinCallCount,
  showsPoliceLateInput,
  showsRegularLateInput,
  sumMeasure,
  thisWeekBucketsFromJobs,
  timelineLabel,
  todayHourBucketsFromJobs,
  todayZonedWindow,
  toIsoWindow,
  usualByHourFromJobs,
  usualByWeekdayFromJobs,
  usualLookbackWindow,
  usualWeekLookbackWindow,
  usualWeekdayAverage,
  viewerTimeZone,
  volumeBucketsFromJobs,
  volumeRowsForAccountType,
  windowForFilter,
  zoneNote
} from '../../utils/scope1Dashboard';
import {
  accountMixAxisLabel,
  accountMixGrainField,
  accountMixHover,
  accountMixInsight,
  accountMixRows,
  companyMeasureRows,
  comparisonInsight,
  crossReportLines,
  formatInvoiceCents,
  hasFullYearHistory,
  namedGrain,
  NEEDS_ATTENTION_ROTATE_MS,
  needsAttentionQueue,
  colorProfitPoints,
  profitVsPaceInsight,
  profitVsPaceMedians,
  profitVsPacePoints,
  QUADRANT_GRID,
  QUADRANT_LABELS,
  assetMeasureRows,
  sumMeasures,
  invoicedBarDetail,
  utilizationAttention,
  utilizationRailTiles,
  volumeInsight,
  windowsInRange
} from '../../utils/overviewUtilization';
import { coverageCompanyIds, grainCoverage, grainFullyCovered, sameLengthPrior } from '../../utils/assetsCrew';
import {
  allHighlightPool,
  buildRail,
  companyHighlightPool,
  highlightWeekday,
  selectHighlightsWhenReady
} from '../../utils/scope1Highlights';
import {
  LATE_ARRIVAL_TOOLTIP,
  NEEDS_ATTENTION_TITLE,
  ON_PACE_SENTENCE,
  ON_PACE_TITLE,
  attentionAlarms,
  buildPace,
  currentActivityLine,
  driverLowFact,
  jobsInElapsedPrior,
  jobsForResponse,
  mixOffFact,
  paceBand,
  pacePosition
} from '../../utils/scope1Pace';

/**
 * @param {PromiseSettledResult<T>} result
 * @returns {T|null}
 * @template T
 */
function settledValue(result) {
  return result.status === 'fulfilled' ? result.value : null;
}

/**
 * @param {Array<PromiseSettledResult<T>>} results
 * @param {number} expected
 * @returns {T[]|null}
 * @template T
 */
function fanoutRows(results, expected) {
  if (!Array.isArray(results) || results.length !== expected) return null;
  if (results.some((result) => result.status !== 'fulfilled' || result.value == null)) {
    return null;
  }
  return results.map((result) => result.value);
}

/**
 * @param {Array<object>} rows
 * @param {string} key
 * @returns {boolean}
 */
function everyKeyed(rows, key) {
  return rows.every((row) => row?.[key] != null);
}

const EMPTY_WINDOW = 'No tows in this window.';
const EMPTY_RESPONSE = 'No response times in this window.';
const EMPTY_COMPARISON = 'No comparison for this window.';
const FAILED_VOLUME = 'Couldn\'t load volume.';
const FAILED_RESPONSE = 'Couldn\'t load response times.';
const FAILED_COMPARISON = 'Couldn\'t load the comparison.';
const FAILED_HIGHLIGHTS = 'Couldn\'t load highlights.';
const FAILED_DRILLDOWN = 'Couldn\'t load jobs in this window.';

const FILTER_LABELS = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};
const EMPTY_COMPANIES = [];
const PIE_COLORS = {
  fast: CHART.new,
  onTime: CHART.today,
  late: CHART.churn
};

function companyReads(companyId) {
  return {
    getAggregates: (params) => jobsService.getAggregates({ ...params, companyId }),
    listJobs: (params) => jobsService.listJobs({ ...params, companyId })
  };
}

function StripTile({
  title,
  subtitle,
  value,
  trailing,
  testId,
  hint,
  compact,
  titleTestId,
  valueTestId,
  subtitleTestId
}) {
  return (
    <section
      className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={testId}
      title={hint}
    >
      <h2 className="text-sm font-medium text-gray-500" data-testid={titleTestId}>{title}</h2>
      <p
        className={`mt-2 flex flex-wrap items-baseline gap-x-2 font-semibold text-gray-900 ${compact ? 'min-h-12 text-sm leading-6' : 'text-2xl md:text-3xl'}`}
      >
        <span data-testid={valueTestId || `${testId}-value`}>{value}</span>
        {trailing ? (
          <span className="text-xs font-medium text-gray-500" data-testid={`${testId}-trailing`}>
            {trailing}
          </span>
        ) : null}
      </p>
      {subtitle ? (
        <p className="mt-1 text-xs text-gray-500" data-testid={subtitleTestId || `${testId}-descriptor`}>{subtitle}</p>
      ) : null}
    </section>
  );
}

function HighlightCard({ item }) {
  return (
    <li
      className="flex h-32 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={`highlight-${item.id}`}
    >
      <p className="line-clamp-2 h-10 text-sm leading-5 text-gray-800" title={item.text}>{item.text}</p>
      <p className="mt-auto text-2xl font-semibold leading-8 text-gray-900" data-testid={`highlight-${item.id}-value`}>
        {item.value ?? '—'}
      </p>
    </li>
  );
}

/**
 * Overview Advanced panel. Same open/close pattern as Assets & Crew
 * ShowSettings. Late inputs stay on page state. No storage.
 *
 * @param {object} props
 */
function OverviewAdvanced({
  open,
  accountType,
  policeMinutes,
  regularMinutes,
  minCallCount,
  onPoliceMinutes,
  onRegularMinutes,
  onMinCallCount,
  onClose
}) {
  if (!open) return null;
  void onClose;
  return (
      <div
        className="z-40 mt-2 w-full rounded-xl border border-gray-200 bg-white p-4 shadow-lg md:absolute md:left-0 md:top-full md:w-80"
        data-testid="overview-advanced-panel"
        role="dialog"
        aria-label="Advanced"
      >
        {showsPoliceLateInput(accountType) ? (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Police Late
            <input
              type="number"
              min={1}
              max={240}
              value={policeMinutes}
              onChange={(event) => onPoliceMinutes(Number(event.target.value) || DEFAULT_POLICE_LATE_MINUTES)}
              className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
              data-testid="police-late-threshold"
            />
            min
          </label>
        ) : null}
        {showsRegularLateInput(accountType) ? (
          <label className={`flex items-center gap-2 text-sm text-gray-600 ${showsPoliceLateInput(accountType) ? 'mt-3' : ''}`}>
            Regular Late
            <input
              type="number"
              min={1}
              max={240}
              value={regularMinutes}
              onChange={(event) => onRegularMinutes(Number(event.target.value) || DEFAULT_REGULAR_LATE_MINUTES)}
              className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
              data-testid="regular-late-threshold"
            />
            min
          </label>
        ) : null}
        <label className="mt-3 block text-sm text-gray-600">
          Minimum calls
          <input
            type="number"
            min={1}
            value={minCallCount}
            onChange={(event) => onMinCallCount(parseMinCallCount(event.target.value))}
            className="ml-2 w-16 rounded-lg border border-gray-200 px-2 py-1 text-gray-900"
            data-testid="overview-min-calls"
          />
        </label>
      </div>
  );
}

/**
 * @param {object} props
 */
export default function Scope1Dashboard({
  now: nowProp,
  view = 'company',
  companies = EMPTY_COMPANIES,
  company = null,
  timeZone: timeZoneProp,
  random = Math.random
}) {
  const now = useMemo(() => nowProp || new Date(), [nowProp]);
  const orgCompanies = useMemo(() => salesOrgCompanies(companies), [companies]);
  const isAll = view === 'all';
  const [filter, setFilter] = useState('day');
  const [metric, setMetric] = useState(RESPONSE_ON_SCENE);
  const [accountType, setAccountType] = useState(ACCOUNT_TYPE_ALL);
  const [policeMinutes, setPoliceMinutes] = useState(DEFAULT_POLICE_LATE_MINUTES);
  const [regularMinutes, setRegularMinutes] = useState(DEFAULT_REGULAR_LATE_MINUTES);
  const [minCallCount, setMinCallCount] = useState(OVERVIEW_MIN_CALLS_DEFAULT);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [windowJobs, setWindowJobs] = useState([]);
  const [jobsStatus, setJobsStatus] = useState('loading');
  const [previousJobs, setPreviousJobs] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparisonFailed, setComparisonFailed] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayAggTotal, setTodayAggTotal] = useState(null);
  const [companyTodayAgg, setCompanyTodayAgg] = useState([]);
  const [lookbackJobs, setLookbackJobs] = useState(null);
  const [lookbackByCompany, setLookbackByCompany] = useState([]);
  const [historyJobs, setHistoryJobs] = useState(null);
  const [lookbackFailed, setLookbackFailed] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [rail, setRail] = useState([]);
  const [utilWindows, setUtilWindows] = useState([]);
  const [utilStatus, setUtilStatus] = useState('loading');
  const [paceDrillCompanyId, setPaceDrillCompanyId] = useState(null);
  const [attentionIndex, setAttentionIndex] = useState(0);
  const [paceAsset, setPaceAsset] = useState('driver');
  const [paceAllAssets, setPaceAllAssets] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const drilldownGen = useRef(0);
  const advancedRef = useRef(null);

  const listedCompany = useMemo(
    () => orgCompanies.find((row) => row.id === company?.id) || null,
    [orgCompanies, company?.id]
  );
  const companyZone = companyClockZone(listedCompany);
  const displayZone = useMemo(() => {
    if (timeZoneProp) return chartTimeZone(timeZoneProp);
    if (isAll) return viewerTimeZone();
    return chartTimeZone(companyZone);
  }, [timeZoneProp, isAll, companyZone]);
  const multiZone = salesOrgHasMultipleTimeZones(orgCompanies);
  const hoursNote = zoneNote({
    view: isAll ? 'all' : 'company',
    multiZone,
    city: company?.city || orgCompanies[0]?.city
  });

  const currentWindow = useMemo(
    () => windowForFilter(filter, now, displayZone),
    [filter, now, displayZone]
  );
  const currentIso = useMemo(() => toIsoWindow(currentWindow), [currentWindow]);
  const previousWindow = useMemo(
    () => previousWindowForFilter(filter, now, displayZone),
    [filter, now, displayZone]
  );
  const previousIso = useMemo(
    () => (previousWindow ? toIsoWindow(previousWindow) : null),
    [previousWindow]
  );
  const pipelineGrain = pipelineGrainForFilter(filter);
  const fanoutIds = useMemo(
    () => (isAll ? orgCompanies.map((row) => row.id) : [undefined]),
    [isAll, orgCompanies]
  );
  const compareWindow = useMemo(
    () => comparisonWindowForFilter(filter, now, displayZone),
    [filter, now, displayZone]
  );

  const typeTokensJobs = useMemo(
    () => [...(windowJobs || []), ...(lookbackJobs || [])],
    [windowJobs, lookbackJobs]
  );
  const typeOptions = useMemo(
    () => accountTypeOptions(typeTokensJobs),
    [typeTokensJobs]
  );
  const populationJobs = useMemo(
    () => (jobsStatus === 'ready' ? filterJobsByAccountType(windowJobs, accountType) : []),
    [jobsStatus, windowJobs, accountType]
  );
  const lookbackPopulation = useMemo(
    () => (lookbackJobs == null ? [] : filterJobsByAccountType(lookbackJobs, accountType)),
    [lookbackJobs, accountType]
  );
  const todayJobs = useMemo(
    () => jobsOnZonedToday(lookbackPopulation, now, displayZone),
    [lookbackPopulation, now, displayZone]
  );
  const usualJobs = useMemo(
    () => jobsOnUsualWeekday(lookbackPopulation, now, displayZone),
    [lookbackPopulation, now, displayZone]
  );
  useEffect(() => {
    setPaceDrillCompanyId(null);
  }, [isAll, company?.id, filter]);

  useEffect(() => {
    let cancelled = false;
    setWindowJobs([]);
    setPreviousJobs(null);
    setRail([]);
    setJobsStatus('loading');
    drilldownGen.current += 1;
    setDrilldown(null);

    Promise.allSettled(fanoutIds.map(async (companyId) => {
      const api = companyReads(companyId);
      const [current, jobs, previous, priorJobs] = await Promise.allSettled([
        api.getAggregates({ grain: pipelineGrain, from: currentIso.from, to: currentIso.to }),
        api.listJobs({ from: currentIso.from, to: currentIso.to, stamp: VOLUME_STAMP }),
        previousIso
          ? api.getAggregates({ grain: pipelineGrain, from: previousIso.from, to: previousIso.to })
          : Promise.resolve(null),
        previousIso
          ? api.listJobs({ from: previousIso.from, to: previousIso.to, stamp: VOLUME_STAMP })
          : Promise.resolve({ jobs: [] })
      ]);
      return {
        companyId,
        current: settledValue(current),
        jobs: settledValue(jobs),
        previous: settledValue(previous),
        previousJobs: settledValue(priorJobs)
      };
    }))
      .then((results) => {
        if (cancelled) return;
        const rows = fanoutRows(results, fanoutIds.length);
        if (!rows) {
          setJobsStatus('failed');
          return;
        }
        const named = (row, list) => (list || []).map((job) => ({
          ...job,
          companyId: job.companyId || row.companyId,
          companyName: orgCompanies.find((entry) => entry.id === (job.companyId || row.companyId))?.businessName
            || job.companyName
        }));
        if (everyKeyed(rows, 'previousJobs')) {
          setPreviousJobs(rows.flatMap((row) => named(row, row.previousJobs?.jobs)));
        }
        if (everyKeyed(rows, 'jobs')) {
          setWindowJobs(rows.flatMap((row) => named(row, row.jobs?.jobs)));
          setJobsStatus('ready');
        } else {
          setJobsStatus('failed');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filter, currentIso.from, currentIso.to, previousIso, pipelineGrain, fanoutIds, orgCompanies]);

  useEffect(() => {
    let cancelled = false;
    setUtilWindows([]);
    setUtilStatus('loading');
    Promise.allSettled(fanoutIds.map(async (companyId) => {
      const payload = await overviewUtilizationService.getOverviewUtilization({ companyId });
      return { companyId, payload };
    }))
      .then((results) => {
        if (cancelled) return;
        const rows = fanoutRows(results, fanoutIds.length);
        if (!rows) {
          setUtilWindows([]);
          setUtilStatus('failed');
          return;
        }
        setUtilWindows(rows.flatMap((row) => (
          (row.payload?.data?.windows || row.payload?.windows || []).map((windowRow) => ({
            ...windowRow,
            companyId: windowRow.companyId || row.companyId || listedCompany?.id,
            companyName: orgCompanies.find((entry) => (
              entry.id === (windowRow.companyId || row.companyId || listedCompany?.id)
            ))?.businessName || windowRow.companyName
          }))
        )));
        setUtilStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setUtilWindows([]);
          setUtilStatus('failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fanoutIds, orgCompanies, listedCompany?.id]);

  useEffect(() => {
    let cancelled = false;
    setHighlights([]);
    setLookbackFailed(false);
    setLookbackJobs(null);
    setLookbackByCompany([]);
    setHistoryJobs(null);
    setTodayAggTotal(null);
    setTodayLoading(true);

    const today = toIsoWindow(todayZonedWindow(now, displayZone));
    const lookback = toIsoWindow(usualLookbackWindow(now, displayZone));

    Promise.allSettled(fanoutIds.map(async (companyId) => {
      const api = companyReads(companyId);
      const [todayAgg, lookbackList] = await Promise.allSettled([
        api.getAggregates({ grain: 'day', from: today.from, to: today.to }),
        api.listJobs({ from: lookback.from, to: today.to, stamp: VOLUME_STAMP })
      ]);
      return {
        companyId,
        todayAgg: settledValue(todayAgg),
        lookbackJobs: settledValue(lookbackList)
      };
    }))
      .then((results) => {
        if (cancelled) return;
        const rows = fanoutRows(results, fanoutIds.length);
        if (!rows) {
          setLookbackFailed(true);
          return;
        }
        const todayBuckets = mergeAggregateBuckets(rows.map((row) => row.todayAgg));
        setTodayAggTotal(everyKeyed(rows, 'todayAgg') ? sumMeasure(todayBuckets) : null);
        setCompanyTodayAgg(orgCompanies.map((entry) => {
          const match = rows.find((row) => row.companyId === entry.id);
          return { name: entry.businessName || entry.id, buckets: match?.todayAgg?.buckets || [] };
        }));
        if (!everyKeyed(rows, 'lookbackJobs')) {
          setLookbackFailed(true);
          return;
        }
        setLookbackJobs(rows.flatMap((row) => (row.lookbackJobs?.jobs || []).map((job) => ({
          ...job,
          companyId: job.companyId || row.companyId,
          companyName: orgCompanies.find((entry) => entry.id === (job.companyId || row.companyId))?.businessName
            || job.companyName
        }))));
        setLookbackByCompany(orgCompanies.map((entry) => {
          const match = rows.find((row) => row.companyId === entry.id);
          return {
            name: entry.businessName || entry.id,
            companyId: entry.id,
            jobs: match?.lookbackJobs?.jobs || []
          };
        }));
        setLookbackFailed(false);
      })
      .finally(() => {
        if (!cancelled) setTodayLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [now, displayZone, fanoutIds, orgCompanies]);

  const hasYear = hasFullYearHistory([
    ...(lookbackJobs || []),
    ...(windowJobs || []),
    ...utilWindows
  ], now);
  const showComparison = showsComparisonChart(filter, { hasYear });

  useEffect(() => {
    let cancelled = false;
    if (!showComparison || !compareWindow) {
      setComparison(null);
      setHistoryJobs(null);
      setComparisonFailed(false);
      setComparisonLoading(false);
      return undefined;
    }

    setComparison(null);
    setComparisonFailed(false);
    setComparisonLoading(true);

    const compareIso = toIsoWindow(compareWindow);
    const lookbackIso = toIsoWindow(
      filter === 'day'
        ? usualWeekLookbackWindow(now, displayZone)
        : usualLookbackWindow(now, displayZone)
    );

    Promise.allSettled(fanoutIds.map(async (companyId) => {
      const api = companyReads(companyId);
      const jobs = await api.listJobs({
        from: (filter === 'hour' || filter === 'day') ? lookbackIso.from : compareIso.from,
        to: compareIso.to,
        stamp: VOLUME_STAMP
      });
      return { companyId, jobs };
    }))
      .then((results) => {
        if (cancelled) return;
        const rows = fanoutRows(results, fanoutIds.length);
        if (!rows) {
          setComparisonFailed(true);
          return;
        }
        if (!everyKeyed(rows, 'jobs')) {
          setComparisonFailed(true);
          return;
        }
        setHistoryJobs(rows.flatMap((row) => row.jobs?.jobs || []));
        const jobs = filterJobsByAccountType(
          rows.flatMap((row) => row.jobs?.jobs || []),
          accountType
        );
        if (filter === 'hour') {
          const todayStart = todayZonedWindow(now, displayZone).from.getTime();
          setComparison({
            mode: 'overlay',
            title: comparisonTitle(filter),
            rows: mergeHourComparison(
              todayHourBucketsFromJobs(jobs, now, displayZone),
              usualByHourFromJobs(
                jobs.filter((job) => Date.parse(job?.enrouteAt || '') < todayStart),
                now,
                displayZone
              ),
              now,
              displayZone
            )
          });
          return;
        }
        if (filter === 'day') {
          const weekStart = compareWindow.from.getTime();
          setComparison({
            mode: 'overlay',
            title: comparisonTitle(filter),
            rows: mergeWeekdayComparison(
              thisWeekBucketsFromJobs(jobs, now, displayZone),
              usualByWeekdayFromJobs(
                jobs.filter((job) => Date.parse(job?.enrouteAt || '') < weekStart),
                displayZone
              ),
              now,
              displayZone
            )
          });
          return;
        }
        setComparison({
          mode: 'history',
          title: comparisonTitle(filter),
          rows: historyComparisonRows(
            volumeBucketsFromJobs(jobs, filter, displayZone),
            filter,
            now,
            displayZone
          )
        });
      })
      .finally(() => {
        if (!cancelled) setComparisonLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter, now, displayZone, fanoutIds, showComparison, compareWindow, accountType]);

  const weekday = highlightWeekday(now, displayZone);
  const usualAvg = lookbackJobs == null ? null : usualWeekdayAverage(usualJobs);
  const todayJobCount = lookbackFailed || lookbackJobs == null ? null : todayJobs.length;
  const todayTotal = accountType === ACCOUNT_TYPE_ALL
    ? todayAggTotal
    : todayJobCount;
  const paceJobs = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const job of [
      ...(lookbackJobs || []),
      ...(windowJobs || []),
      ...(previousJobs || []),
      ...(historyJobs || [])
    ]) {
      const key = job?.id || `${job?.completedAt || ''}:${job?.enrouteAt || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(job);
    }
    return filterJobsByAccountType(out, accountType);
  }, [lookbackJobs, windowJobs, previousJobs, historyJobs, accountType]);
  const lookbackSettled = lookbackFailed || lookbackJobs != null;
  const todayPace = useMemo(
    () => (lookbackFailed || lookbackJobs == null
      ? null
      : buildPace({ jobs: lookbackPopulation, filter: 'day', now, timeZone: displayZone })),
    [lookbackFailed, lookbackJobs, lookbackPopulation, now, displayZone]
  );
  const filterPace = useMemo(
    () => {
      if (!lookbackSettled || lookbackFailed) return null;
      return buildPace({ jobs: paceJobs, filter, now, timeZone: displayZone });
    },
    [lookbackSettled, lookbackFailed, paceJobs, filter, now, displayZone]
  );
  const mixOff = useMemo(
    () => (lookbackJobs == null ? null : mixOffFact(todayJobs, usualJobs)),
    [lookbackJobs, todayJobs, usualJobs]
  );
  const driverLow = useMemo(
    () => (lookbackJobs == null
      ? null
      : driverLowFact({
        jobs: lookbackPopulation,
        now,
        timeZone: displayZone,
        minCalls: minCallCount
      })),
    [lookbackJobs, lookbackPopulation, now, displayZone, minCallCount]
  );

  useEffect(() => {
    if (!advancedOpen) return undefined;
    const onPointerDown = (event) => {
      if (!advancedRef.current?.contains(event.target)) {
        setAdvancedOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [advancedOpen]);

  useEffect(() => {
    if (todayLoading) return;
    if (lookbackFailed || lookbackJobs == null) {
      setHighlights([]);
      return;
    }
    const companyUsual = lookbackByCompany.map((entry) => {
      const jobs = jobsOnUsualWeekday(
        filterJobsByAccountType(entry.jobs, accountType),
        now,
        displayZone
      );
      return {
        name: entry.name,
        buckets: [{ enrouteCount: usualWeekdayAverage(jobs) }]
      };
    });
    const companyToday = accountType === ACCOUNT_TYPE_ALL
      ? companyTodayAgg
      : lookbackByCompany.map((entry) => ({
        name: entry.name,
        buckets: [{
          enrouteCount: jobsOnZonedToday(
            filterJobsByAccountType(entry.jobs, accountType),
            now,
            displayZone
          ).length
        }]
      }));
    const companyPace = Object.fromEntries(lookbackByCompany.map((entry) => [
      entry.name,
      buildPace({
        jobs: filterJobsByAccountType(entry.jobs, accountType),
        filter: 'day',
        now,
        timeZone: displayZone
      })
    ]));
    const pool = isAll
      ? allHighlightPool({
        companyToday,
        companyUsual,
        todayJobs,
        usualJobs,
        weekday,
        mixOff,
        companyPace,
        policeMinutes,
        regularMinutes,
        accountType
      })
      : companyHighlightPool({
        todayTotal,
        usualTotal: usualAvg,
        todayJobs,
        usualJobs,
        weekday,
        metric: RESPONSE_ON_SCENE,
        policeMinutes,
        regularMinutes,
        accountType,
        pace: todayPace,
        mixOff
      });
    setHighlights(selectHighlightsWhenReady(pool, todayPace?.current ?? todayJobs.length, {
      random,
      weekday
    }));
  }, [
    todayLoading,
    lookbackFailed,
    lookbackJobs,
    lookbackByCompany,
    companyTodayAgg,
    accountType,
    now,
    displayZone,
    isAll,
    todayJobs,
    usualJobs,
    weekday,
    todayTotal,
    usualAvg,
    policeMinutes,
    regularMinutes,
    random,
    todayPace,
    mixOff
  ]);

  useEffect(() => {
    if (jobsStatus !== 'ready') {
      setRail([]);
      return;
    }
    const groups = isAll
      ? lookbackByCompany.map((entry) => {
        const typed = filterJobsByAccountType(entry.jobs, accountType);
        return {
          name: entry.name,
          jobs: populationJobs.filter((job) => (
            job.companyId === entry.companyId || job.companyName === entry.name
          )),
          usualJobs: jobsOnUsualWeekday(typed, now, displayZone),
          paceJobs: typed
        };
      })
      : Object.entries(
        populationJobs.reduce((acc, job) => {
          const name = job.accountName;
          if (!name) return acc;
          acc[name] = acc[name] || [];
          acc[name].push(job);
          return acc;
        }, {})
      ).map(([name, jobs]) => ({
        name,
        jobs,
        usualJobs: usualJobs.filter((job) => job.accountName === name),
        paceJobs: lookbackPopulation.filter((job) => job.accountName === name)
      }));
    setRail(buildRail(isAll ? 'all' : 'company', {
      jobs: populationJobs,
      usualJobs,
      todayJobs: populationJobs,
      groups,
      metric,
      policeMinutes,
      regularMinutes,
      accountType,
      now,
      timeZone: displayZone,
      weekday,
      occupiedTypes: highlights.map((item) => item.id),
      occupiedNames: highlights.map((item) => item.name).filter(Boolean),
      minCalls: minCallCount
    }));
  }, [
    jobsStatus,
    isAll,
    populationJobs,
    usualJobs,
    lookbackByCompany,
    lookbackPopulation,
    accountType,
    now,
    displayZone,
    metric,
    policeMinutes,
    regularMinutes,
    weekday,
    highlights,
    minCallCount
  ]);

  const volumeFailed = jobsStatus === 'failed';
  const volumeRows = volumeRowsForAccountType({
    jobs: jobsStatus === 'ready' ? populationJobs : null,
    jobsFailed: jobsStatus === 'failed',
    filter,
    timeZone: displayZone,
    now
  });
  const total = jobsStatus === 'ready' ? populationJobs.length : null;
  const volumeEmpty = jobsStatus === 'ready' && volumeRows != null && isEmptyVolume(volumeRows);
  const responseFailed = jobsStatus === 'failed';
  const responseSet = jobsStatus === 'ready' ? jobsForResponse(populationJobs) : { jobs: [], excludedCount: 0 };
  const avgSeconds = jobsStatus === 'ready' ? meanDuration(responseSet.jobs, metric) : null;
  const late = jobsStatus === 'ready'
    ? lateRateByType(responseSet.jobs, policeMinutes, regularMinutes, metric, accountType)
    : { sample: 0, late: 0, rate: null };
  const pie = jobsStatus === 'ready'
    ? responsePieSlices(responseSet.jobs, policeMinutes, regularMinutes, metric, accountType)
    : { sample: 0, bins: [] };
  const responseEmpty = jobsStatus === 'ready' && pie.sample === 0;
  const pieRows = pie.bins.map((bin) => ({
    ...bin,
    color: PIE_COLORS[bin.key] || CHART.brand
  }));
  const usualResponse = lookbackJobs == null ? { jobs: [] } : jobsForResponse(usualJobs);
  const usualLate = lookbackJobs == null
    ? null
    : lateRateByType(usualResponse.jobs, policeMinutes, regularMinutes, metric, accountType);
  const pieSentence = pieLateSentence({
    lateRate: late.rate,
    usualLateRate: usualLate?.rate,
    weekday
  });
  const lateBand = usualLate?.rate == null
    ? { usual: null }
    : paceBand([Number(usualLate.rate) * 100]);
  const latePosition = late.rate == null || lateBand.usual == null
    ? 'unknown'
    : pacePosition(Number(late.rate) * 100, lateBand);
  const occupiedFacts = highlights.map((item) => item.id);
  const occupiedNames = highlights.map((item) => item.name).filter(Boolean);
  const analysisLines = (!lookbackSettled || lookbackFailed)
    ? []
    : attentionAlarms({
      lateRate: late.rate,
      usualLateRate: usualLate?.rate,
      latePosition,
      lateCount: late.late,
      lateSample: late.sample,
      volumePosition: filterPace?.position,
      mixOff,
      pace: filterPace,
      driverLow,
      occupiedFacts,
      occupiedNames
    });
  const grainName = namedGrain({
    view: isAll ? 'all' : 'company',
    companyName: listedCompany?.businessName || company?.businessName,
    salesOrgName: company?.salesOrgName || listedCompany?.salesOrgName
  });
  const utilView = isAll ? 'all' : 'company';
  const scopedWindows = useMemo(
    () => windowsInRange(utilWindows, currentWindow, null, {
      filter,
      timeZone: displayZone,
      now
    }),
    [utilWindows, currentWindow, filter, displayZone, now]
  );
  const priorWindows = useMemo(
    () => windowsInRange(utilWindows, previousWindow, currentWindow, {
      filter,
      timeZone: displayZone,
      now
    }),
    [utilWindows, previousWindow, currentWindow, filter, displayZone, now]
  );
  const selectionOpts = useMemo(
    () => ({ range: currentWindow, timeZone: displayZone, now }),
    [currentWindow, displayZone, now]
  );
  const companyUtilRows = useMemo(
    () => companyMeasureRows(scopedWindows, filter, { ...selectionOpts, preselected: true }),
    [scopedWindows, filter, selectionOpts]
  );
  const truckRows = useMemo(
    () => assetMeasureRows(scopedWindows, 'truck', filter, { ...selectionOpts, preselected: true }),
    [scopedWindows, filter, selectionOpts]
  );
  const driverRows = useMemo(
    () => assetMeasureRows(scopedWindows, 'driver', filter, { ...selectionOpts, preselected: true }),
    [scopedWindows, filter, selectionOpts]
  );
  const currentUtilMeasures = useMemo(
    () => (isAll
      ? sumMeasures(companyUtilRows.map((row) => row.measures))
      : (companyUtilRows[0]?.measures || sumMeasures(truckRows.map((row) => row.measures)))),
    [isAll, companyUtilRows, truckRows]
  );
  const priorCompanyUtilRows = useMemo(
    () => companyMeasureRows(priorWindows, filter, {
      range: previousWindow,
      timeZone: displayZone,
      now,
      preselected: true
    }),
    [priorWindows, filter, previousWindow, displayZone, now]
  );
  const previousUtilMeasures = useMemo(
    () => sumMeasures(priorCompanyUtilRows.map((row) => row.measures)),
    [priorCompanyUtilRows]
  );
  const invoiceCoverageCompanyIds = useMemo(
    () => coverageCompanyIds(utilWindows, filter, [currentWindow, previousWindow]),
    [utilWindows, filter, currentWindow, previousWindow]
  );
  const invoiceComparable = useMemo(
    () => grainFullyCovered(utilWindows, filter, currentWindow, displayZone, {
      companyIds: invoiceCoverageCompanyIds
    })
      && grainFullyCovered(utilWindows, filter, previousWindow, displayZone, {
        companyIds: invoiceCoverageCompanyIds
      }),
    [utilWindows, filter, currentWindow, previousWindow, displayZone, invoiceCoverageCompanyIds]
  );
  const utilAttentionLines = useMemo(
    () => utilizationAttention({
      view: utilView,
      companyRows: companyUtilRows,
      priorCompanyRows: priorCompanyUtilRows,
      truckRows,
      driverRows,
      filter,
      minCalls: minCallCount,
      invoiceComparable
    }),
    [utilView, companyUtilRows, priorCompanyUtilRows, truckRows, driverRows, filter, minCallCount, invoiceComparable]
  );
  const crossLines = useMemo(
    () => crossReportLines({
      view: utilView,
      filter,
      companyRows: companyUtilRows,
      truckRows,
      driverRows,
      jobs: populationJobs,
      metric,
      minCalls: minCallCount
    }),
    [utilView, filter, companyUtilRows, truckRows, driverRows, populationJobs, metric, minCallCount]
  );
  const attentionQueue = (!lookbackSettled || lookbackFailed)
    ? []
    : needsAttentionQueue({
      analysis: analysisLines,
      utilization: utilAttentionLines,
      cross: crossLines,
      occupiedNames,
      occupiedFacts
    });
  const attentionQueueKey = `${filter}|${isAll ? 'all' : (company?.id || '')}|${attentionQueue.map((line) => `${line.id}\t${line.sentence}`).join('\n')}`;
  const [attentionQueueKeySeen, setAttentionQueueKeySeen] = useState(attentionQueueKey);
  let attentionCursor = attentionIndex;
  if (attentionQueueKeySeen !== attentionQueueKey) {
    setAttentionQueueKeySeen(attentionQueueKey);
    setAttentionIndex(0);
    attentionCursor = 0;
  }
  const shownAttention = attentionQueue.length
    ? attentionQueue[attentionCursor % attentionQueue.length]
    : null;
  const volumeKnown = filterPace?.position !== 'unknown' && filterPace?.position != null;
  const showOnPace = lookbackSettled
    && !lookbackFailed
    && attentionQueue.length === 0
    && analysisLines.length === 0
    && volumeKnown;
  const attention = {
    title: shownAttention ? NEEDS_ATTENTION_TITLE : (showOnPace ? ON_PACE_TITLE : null),
    sentence: shownAttention ? shownAttention.sentence : (showOnPace ? ON_PACE_SENTENCE : null)
  };
  const kpiSentence = attention.sentence;
  useEffect(() => {
    if (attentionQueue.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setAttentionIndex((current) => (current + 1) % attentionQueue.length);
    }, NEEDS_ATTENTION_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [attentionQueueKey, attentionQueue.length]);
  const currentCoverage = useMemo(
    () => grainCoverage(utilWindows, filter, currentWindow, displayZone),
    [utilWindows, filter, currentWindow, displayZone]
  );
  const invoicedDetail = invoicedBarDetail({
    view: utilView,
    filter,
    currentMeasures: currentUtilMeasures,
    previousMeasures: previousUtilMeasures,
    invoiceComparable,
    coverage: currentCoverage,
    companyRows: companyUtilRows
  });
  const utilRail = utilizationRailTiles({
    view: isAll ? 'all' : 'company',
    companyRows: companyUtilRows,
    truckRows,
    driverRows
  });
  const railTiles = [...rail, ...utilRail];
  const paceView = paceDrillCompanyId ? 'company' : (isAll ? 'all' : 'company');
  const paceCompanyRows = paceDrillCompanyId
    ? companyUtilRows.filter((row) => row.companyId === paceDrillCompanyId)
    : companyUtilRows;
  const paceWindows = paceDrillCompanyId
    ? scopedWindows.filter((row) => row.companyId === paceDrillCompanyId)
    : scopedWindows;
  const paceJobsForChart = paceDrillCompanyId
    ? populationJobs.filter((job) => job.companyId === paceDrillCompanyId)
    : populationJobs;
  const paceAssetPoints = profitVsPacePoints({
    view: 'company',
    truckRows: assetMeasureRows(paceWindows, 'truck', filter),
    driverRows: assetMeasureRows(paceWindows, 'driver', filter),
    jobs: paceJobsForChart,
    metric,
    minCalls: minCallCount
  });
  const paceCompanyPoints = profitVsPacePoints({
    view: 'all',
    companyRows: paceCompanyRows,
    jobs: paceJobsForChart,
    metric,
    minCalls: minCallCount
  });
  const plottingCompanies = paceView === 'all' && !paceAllAssets;
  const pacePoints = plottingCompanies
    ? paceCompanyPoints
    : paceAssetPoints.filter((point) => point.grain === paceAsset);
  const paceMedians = profitVsPaceMedians(pacePoints);
  const paceChartPoints = colorProfitPoints(pacePoints, paceMedians);
  const paceInsight = profitVsPaceInsight({
    view: plottingCompanies ? 'all' : 'company',
    points: pacePoints,
    medians: paceMedians,
    filter
  });
  const mixField = accountMixGrainField(accountType);
  const mixAxis = accountMixAxisLabel(accountType);
  const mixRows = accountMixRows({
    jobs: populationJobs,
    field: mixField
  });
  const previousTyped = previousJobs == null
    ? null
    : filterJobsByAccountType(previousJobs, accountType);
  const elapsedPriorJobs = sameLengthPrior(filter) && previousTyped != null
    ? jobsInElapsedPrior(previousTyped, currentWindow, previousWindow, now, displayZone)
    : previousTyped;
  const mixPreviousRows = accountMixRows({
    jobs: elapsedPriorJobs || [],
    field: mixField
  });
  const mixInsight = accountMixInsight({
    view: isAll ? 'all' : 'company',
    filter,
    currentRows: mixRows,
    previousRows: mixPreviousRows,
    selectedType: mixField === 'accountName' ? accountType : null,
    grainName
  });
  const volumeLine = jobsStatus === 'ready'
    ? volumeInsight({
      total,
      usual: sameLengthPrior(filter) && elapsedPriorJobs != null
        ? elapsedPriorJobs.length
        : null,
      filter,
      grainName
    })
    : null;
  const comparisonLine = !comparisonLoading && !comparisonFailed && comparison?.rows
    ? comparisonInsight({
      filter,
      rows: comparison.rows,
      grainName
    })
    : null;
  const activityLine = todayPace ? currentActivityLine(todayPace) : null;
  const comparisonEmpty = !comparisonLoading && Array.isArray(comparison?.rows)
    && comparison.rows.every((row) => {
      if (comparison.mode === 'history') return !row.tows;
      return !row.today && !row.usual;
    });
  const comparisonLegend = comparison?.mode === 'history'
    ? [
      { label: 'Prior', color: USUAL_COLOR },
      { label: 'In progress', color: TODAY_COLOR }
    ]
    : [
      { label: 'Happened', color: TODAY_COLOR },
      { label: '12 week avg.', color: USUAL_COLOR }
    ];
  const pieLegend = [
    { label: 'Early', color: PIE_COLORS.fast },
    { label: 'On time', color: PIE_COLORS.onTime },
    { label: 'Late', color: PIE_COLORS.late }
  ];
  const highlightsFailed = lookbackFailed;

  const openDrilldown = useCallback((from, to, expectedCount) => {
    drilldownGen.current += 1;
    if (jobsStatus !== 'ready') {
      setDrilldown({ from, to, expectedCount, jobs: [], failed: true });
      setDrilldownLoading(false);
      return;
    }
    setDrilldown({
      from,
      to,
      expectedCount,
      jobs: jobsInWindow(populationJobs, { from: new Date(from), to: new Date(to) }),
      failed: false
    });
    setDrilldownLoading(false);
  }, [jobsStatus, populationJobs]);

  const closeDrilldown = useCallback(() => {
    drilldownGen.current += 1;
    setDrilldown(null);
    setDrilldownLoading(false);
  }, []);

  return (
    <div className="space-y-5 overflow-x-hidden" data-testid="scope1-dashboard" data-view={isAll ? 'all' : 'company'}>
      <section
        data-testid="scope1-today-band"
        className="flex flex-col gap-4 lg:flex-row lg:items-start"
      >
        <div className="min-w-0 flex-1" data-testid="today-highlights">
          <h2 className="text-md font-semibold text-gray-900">Today&apos;s Highlights</h2>
          {todayLoading ? (
            <p className="mt-2 text-sm text-gray-500">Loading…</p>
          ) : highlightsFailed ? (
            <p className="mt-2 text-sm text-gray-500">{FAILED_HIGHLIGHTS}</p>
          ) : highlights.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No highlights for today.</p>
          ) : (
            <ul className={`mt-3 grid grid-cols-1 gap-3 ${
              highlights.length >= 3 ? 'md:grid-cols-3' : highlights.length === 2 ? 'md:grid-cols-2' : ''
            }`}>
              {highlights.map((item) => (
                <HighlightCard key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>
        <hr className="border-gray-200 lg:hidden" data-testid="scope1-divider" />
        <div
          className="hidden w-px self-stretch bg-gray-200 lg:block"
          data-testid="scope1-divider-vert"
          aria-hidden="true"
        />
        <div className="lg:w-64 lg:shrink-0" data-testid="today-activity">
          <h2 className="text-md font-semibold text-gray-900">Current Activity</h2>
          <section
            className="mt-3 flex h-16 items-center gap-3 overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 shadow-sm lg:h-32 lg:flex-col lg:items-stretch lg:p-4"
            data-testid="today-score"
          >
            <p
              className="line-clamp-2 min-w-0 flex-1 text-sm leading-5 text-gray-600 lg:h-10 lg:flex-none"
              data-testid="today-score-usual"
              title={activityLine || undefined}
            >
              {todayLoading ? 'Tows' : activityLine || 'Tows'}
            </p>
            <p className="shrink-0 text-2xl font-semibold leading-8 text-gray-900 lg:mt-auto" data-testid="today-score-value">
              {todayLoading ? '…' : todayPace == null ? '—' : formatVolume(todayPace.current)}
            </p>
          </section>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3" data-testid="scope1-filters">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Time
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-gray-900"
            aria-label="Time filter"
            data-testid="time-filter"
          >
            {TIME_FILTERS.map((value) => (
              <option key={value} value={value}>{FILTER_LABELS[value]}</option>
            ))}
          </select>
        </label>
        <div className="flex overflow-hidden rounded-lg border border-gray-200 text-sm" role="group" aria-label="Response metric">
          {[
            { value: RESPONSE_ON_SCENE, label: 'Enroute to on scene' },
            { value: RESPONSE_COMPLETED, label: 'Enroute to completed' }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              className={`px-3 py-1 ${
                metric === option.value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
              }`}
              onClick={() => setMetric(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Account Type
          <select
            value={accountType}
            onChange={(event) => setAccountType(event.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-gray-900"
            aria-label="Account Type"
            data-testid="account-type-filter"
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <div className="relative max-md:w-full" data-testid="overview-advanced-control" ref={advancedRef}>
          <button
            type="button"
            className={`rounded-lg border px-3 py-1 text-sm ${
              advancedOpen ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700'
            }`}
            data-testid="overview-advanced"
            aria-expanded={advancedOpen}
            aria-haspopup="dialog"
            onClick={() => setAdvancedOpen((open) => !open)}
          >
            Advanced
          </button>
          <OverviewAdvanced
            open={advancedOpen}
            accountType={accountType}
            policeMinutes={policeMinutes}
            regularMinutes={regularMinutes}
            minCallCount={minCallCount}
            onPoliceMinutes={setPoliceMinutes}
            onRegularMinutes={setRegularMinutes}
            onMinCallCount={setMinCallCount}
            onClose={() => setAdvancedOpen(false)}
          />
        </div>
      </div>

      <section
        className="rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm"
        data-testid="scope1-attention-strip"
      >
        <div
          className="flex items-center gap-3"
          data-testid="scope1-performance"
        >
          <div className="flex min-w-0 flex-1 items-start gap-2 text-gray-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="min-w-0">
              <h2
                className="text-sm font-semibold"
                data-testid="scope1-performance-title"
              >
                {attention.title || '—'}
              </h2>
              <p
                className="mt-0.5 text-sm leading-5 text-gray-800"
                data-testid="scope1-performance-sentence"
              >
                {jobsStatus === 'loading' && !lookbackSettled ? '…' : kpiSentence || '—'}
              </p>
            </div>
          </div>
          {attentionQueue.length > 0 ? (
            <div className="flex shrink-0 flex-col items-center gap-0.5">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700"
                  aria-label="Previous attention"
                  data-testid="scope1-attention-prev"
                  onClick={() => setAttentionIndex((current) => (
                    (current - 1 + attentionQueue.length) % attentionQueue.length
                  ))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700"
                  aria-label="Next attention"
                  data-testid="scope1-attention-next"
                  onClick={() => setAttentionIndex((current) => (
                    (current + 1) % attentionQueue.length
                  ))}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <span className="text-xs text-gray-500" data-testid="scope1-attention-count">
                {`${(attentionCursor % attentionQueue.length) + 1} of ${attentionQueue.length}`}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      <p className="text-sm font-medium text-gray-700" data-testid="scope1-timeline">
        {timelineLabel(filter)}
      </p>
      {hoursNote ? (
        <p className="text-sm text-gray-500" data-testid="zone-note">{hoursNote}</p>
      ) : null}

      <div
        className="grid grid-cols-2 gap-4 xl:grid-cols-4"
        data-testid="scope1-strip"
      >
        <StripTile
          title="Tows"
          value={jobsStatus === 'loading' ? '…' : volumeFailed ? '—' : formatVolume(total)}
          testId="strip-tows"
        />
        <StripTile
          title="Invoiced"
          value={
            utilStatus === 'loading'
              ? '…'
              : utilStatus === 'failed'
                ? '—'
                : scopedWindows.length === 0
                  ? 'no data'
                  : formatInvoiceCents(currentUtilMeasures?.totalInvoiceCents)
          }
          subtitle={invoicedDetail}
          subtitleTestId="utilization-pulse"
          testId="strip-invoiced"
        />
        <StripTile
          title="Average response"
          value={jobsStatus === 'loading' ? '…' : responseFailed ? '—' : formatSeconds(avgSeconds)}
          testId="strip-response"
        />
        <StripTile
          title="% late"
          value={jobsStatus === 'loading' ? '…' : responseFailed || late.rate == null ? '—' : `${Math.round(late.rate * 100)}%`}
          testId="strip-late"
          hint={LATE_ARRIVAL_TOOLTIP}
          trailing={jobsStatus === 'ready' && responseSet.excludedCount > 0
            ? `${responseSet.excludedCount} scheduled excluded`
            : null}
        />
      </div>

      <div
        className="grid grid-cols-1 gap-6"
        data-testid="scope1-columns"
      >
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:items-stretch" data-testid="scope1-volume-tiles">
        <div className="min-w-0" data-testid="scope1-volume-block">
          <ChartCard title="Volume" caption={volumeLine} captionTestId="volume-insight">
            {jobsStatus === 'loading' ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : volumeFailed ? (
              <ChartEmpty text={FAILED_VOLUME} />
            ) : volumeEmpty ? (
              <ChartEmpty text={EMPTY_WINDOW} />
            ) : (
              <ReportingLineChart
                data={volumeRows || []}
                dataKey="enrouteCount"
                name="Tows"
                valueFormatter={formatVolume}
                yAxisLabel="Tows"
                xAxisLabel={filter === 'hour' ? 'Hour' : 'Time'}
                onPointClick={(row) => {
                  if (!row?.bucketStart || !row?.bucketEnd) return;
                  openDrilldown(row.bucketStart, row.bucketEnd, Number(row.enrouteCount) || 0);
                }}
              />
            )}
          </ChartCard>
        </div>

        <div className="min-w-0" data-testid="scope1-right-rail">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="scope1-rail">
            {railTiles.map((item, index) => (
              <section
                key={`${item.title}-${item.label}`}
                className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${
                  railTiles.length % 2 === 1 && index === railTiles.length - 1 ? 'md:col-span-2' : ''
                }`}
              >
                <h3 className="text-sm font-medium text-gray-500">{item.title}</h3>
                <p className="mt-2 text-lg font-semibold text-gray-900">{item.label}</p>
                <p className="mt-1 text-sm text-gray-600">{item.detail}</p>
              </section>
            ))}
          </div>
        </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:items-stretch" data-testid="scope1-chart-rows">
        {showComparison ? (
          <div className="min-w-0" data-testid="scope1-comparison-block">
            <ChartCard
              title={comparison?.title || comparisonTitle(filter)}
              action={<ChartLegend items={comparisonLegend} />}
              caption={comparisonLine}
              captionTestId="comparison-insight"
              className="h-full"
            >
              {comparisonLoading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : comparisonFailed ? (
                <ChartEmpty text={FAILED_COMPARISON} />
              ) : comparisonEmpty ? (
                <ChartEmpty text={EMPTY_COMPARISON} />
              ) : comparison?.mode === 'history' ? (
                <ReportingBarChart
                  data={comparison.rows}
                  bars={[{ dataKey: 'tows', name: 'Tows', color: USUAL_COLOR }]}
                  valueFormatter={formatVolume}
                  yAxisLabel="Tows"
                />
              ) : (
                <ReportingBarChart
                  data={comparison?.rows || []}
                  bars={[
                    { dataKey: 'today', name: 'Happened', color: TODAY_COLOR },
                    { dataKey: 'usual', name: '12 week avg.', color: USUAL_COLOR }
                  ]}
                  valueFormatter={formatVolume}
                  yAxisLabel="Tows"
                />
              )}
            </ChartCard>
          </div>
        ) : null}

        <div className="min-w-0" data-testid="profit-vs-pace">
          <ChartCard
            title="Invoice per call vs response time"
            caption={paceInsight}
            captionTestId="profit-vs-pace-insight"
            fixedPlot
            action={(
              <div className="flex flex-wrap items-center justify-end gap-3">
                <div
                  className="grid grid-cols-2 gap-x-3 gap-y-1 text-left"
                  data-testid="profit-quadrant-labels"
                >
                  {QUADRANT_GRID.map((key) => {
                    const item = QUADRANT_LABELS[key];
                    return (
                      <span
                        key={key}
                        data-tone={item.tone}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: item.color }}
                          data-testid={`quadrant-swatch-${key}`}
                        />
                        {item.label}
                      </span>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="flex overflow-hidden rounded-lg border border-gray-200 text-sm"
                    role="group"
                    aria-label="Invoice per call vs response time"
                  >
                    {[
                      { value: 'driver', label: 'Drivers' },
                      { value: 'truck', label: 'Trucks' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`px-3 py-1 ${
                          !plottingCompanies && paceAsset === option.value
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-700'
                        }`}
                        data-testid={`pace-asset-${option.value}`}
                        onClick={() => {
                          setPaceAsset(option.value);
                          if (isAll && !paceDrillCompanyId) setPaceAllAssets(true);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {paceDrillCompanyId || paceAllAssets ? (
                    <button
                      type="button"
                      className="text-sm text-gray-600 underline"
                      data-testid="profit-vs-pace-back"
                      onClick={() => {
                        if (paceDrillCompanyId) setPaceDrillCompanyId(null);
                        setPaceAllAssets(false);
                      }}
                    >
                      Back
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          >
            {pacePoints.length === 0 ? (
              <ChartEmpty text="No points for this window." />
            ) : (
              <ReportingScatterChart
                data={paceChartPoints}
                xMedian={paceMedians.x}
                yMedian={paceMedians.y}
                xAxisLabel={metric === RESPONSE_COMPLETED
                  ? 'Median response (enroute to completed)'
                  : 'Median response (enroute to on scene)'}
                xFormatter={(value) => formatSeconds(value)}
                yFormatter={(value) => formatInvoiceCents(value)}
                onPointClick={isAll && !paceDrillCompanyId && !paceAllAssets
                  ? (row) => {
                    const point = chartEventRow(row);
                    if (point?.companyId) setPaceDrillCompanyId(point.companyId);
                  }
                  : undefined}
              />
            )}
          </ChartCard>
        </div>

        <div className="min-w-0" data-testid="account-mix">
          <ChartCard
            title="Account Mix"
            caption={mixInsight}
            captionTestId="account-mix-insight"
            fixedPlot
          >
            {mixRows.length === 0 ? (
              <ChartEmpty text="No account mix for this time." />
            ) : (
              <ReportingBarChart
                data={mixRows}
                bars={[{ dataKey: 'value', name: 'Calls', color: USUAL_COLOR }]}
                valueFormatter={formatVolume}
                xAxisLabel={mixAxis}
                hideCategoryTicks
                hoverLine={(row) => accountMixHover({ row })}
              />
            )}
          </ChartCard>
        </div>

        <section className="min-w-0" data-testid="scope1-response-pie">
          <ChartCard
            title="Response"
            action={<ChartLegend items={pieLegend} />}
            caption={jobsStatus === 'ready' ? pieSentence : null}
            className="h-full"
          >
            {jobsStatus === 'loading' ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : responseFailed ? (
              <ChartEmpty text={FAILED_RESPONSE} />
            ) : responseEmpty ? (
              <ChartEmpty text={EMPTY_RESPONSE} />
            ) : (
              <ReportingPieChart data={pieRows} />
            )}
          </ChartCard>
        </section>

        </div>
      </div>

      {drilldown ? (
        <section
          className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
          data-testid="scope1-drilldown"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-md font-semibold text-gray-900">Jobs in this window</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {drilldown.from} → {drilldown.to}
              </p>
            </div>
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-900"
              onClick={closeDrilldown}
            >
              Close
            </button>
          </div>
          {drilldownLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : drilldown.failed ? (
            <p className="text-sm text-gray-500" data-testid="drilldown-failed">{FAILED_DRILLDOWN}</p>
          ) : drilldown.jobs.length === 0 ? (
            <p className="text-sm text-gray-500">{EMPTY_WINDOW}</p>
          ) : (
            <div>
              <p className="mb-3 text-sm text-gray-600" data-testid="drilldown-count">
                {drilldown.jobs.length} jobs
                {drilldown.expectedCount != null
                  ? ` · aggregate ${drilldown.expectedCount}`
                  : ''}
              </p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2 pr-4 font-medium">Job</th>
                    <th className="py-2 pr-4 font-medium">Enroute</th>
                    <th className="py-2 pr-4 font-medium">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {drilldown.jobs.map((job) => (
                    <tr key={job.id} className="border-t border-gray-100 text-gray-900">
                      <td className="py-2 pr-4">{job.externalId || job.id}</td>
                      <td className="py-2 pr-4">{job.enrouteAt || '—'}</td>
                      <td className="py-2 pr-4">{job.completedAt || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
